import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { Redirect } from 'react-router';
import { toast } from 'sonner';
import { useChargifyOffersQuery } from '../../../../queries/Admin/CreateAccount';
import { useGetSignUpUrl } from '../../../../queries/Admin/CreateAccount/useGetSignUpUrl';
import { LoadingSpinner } from '../../../../web/components/LoadingSpinner';
import { baseStyleSheet } from '../../../../web/styles/styles';
import { ICreateAccountRequest } from '../../../models/Accounts';
import { useCreateAccount } from '../CreateAccountV2/hooks';
import { styleSheet } from '../CreateAccountV2/styles';

interface IProps {
	createAccountRequest?: ICreateAccountRequest;
}

export const CreateAccountV2Step2: React.FC<IProps> = observer(props => {
	const { nextDisplayImage, setNextDisplayImage, chargifyOfferLink, setChargifyOfferLink, getRandomImage } =
		useCreateAccount();
	const { createAccountRequest } = props;
	const account = createAccountRequest?.account;

	const [selectedOffer, setSelectedOffer] = React.useState<Api.IChargifyOffer>();
	const [getSignUpUrlEnabled, setGetSignUpUrlEnabled] = React.useState<boolean>(false);
	const [accountComplete, setAccountComplete] = React.useState<boolean>(false);
	const { data: chargifyOffers, isPending: offersLoading } = useChargifyOffersQuery();
	const {
		data: signUpUrl,
		isFetched: signUpFetched,
		isFetching: isFetchingSignUpUrl,
		error,
	} = useGetSignUpUrl({
		accountId: account?.id || '',
		offerUrl: selectedOffer ? '' : chargifyOfferLink || '',
		offerId: selectedOffer ? selectedOffer.id : undefined,
		enabled: getSignUpUrlEnabled,
	});
	React.useEffect(() => {
		if (error) {
			setGetSignUpUrlEnabled(false);
			setChargifyOfferLink('');
			toast.error('Invalid Chargify Offer Id or Link');
		}
	}, [error, setChargifyOfferLink]);

	const onChangeOffer = (e: React.MouseEvent<HTMLButtonElement>) => {
		const offer = chargifyOffers?.find(o => o.id.toString() === e.currentTarget.value);
		if (!offer) {
			return;
		}
		if (selectedOffer?.id === offer.id) {
			setSelectedOffer(undefined);
			setChargifyOfferLink('');
			return;
		}
		setSelectedOffer(offer);
		setChargifyOfferLink('');
	};

	const renderChargifyOffers = () => {
		if (chargifyOffers) {
			const offers = Api.VmUtils.dedupeById(chargifyOffers);
			return offers.map((offer: Api.IChargifyOffer) => (
				<button
					className={css(
						styleSheet.fieldContainerHighlight,
						selectedOffer?.id === offer.id ? styleSheet.fieldContainerSelected : null
					)}
					onClick={onChangeOffer}
					key={offer.id}
					value={offer.id.toString()}
				>
					{offer.name}
				</button>
			));
		}
	};

	const renderCompleteRedirect = () => {
		return account && account.id ? <Redirect to={`/accounts/${account.id}`} /> : null;
	};

	const renderFormContent = () => {
		if (nextDisplayImage && account) {
			return (
				<div className={css(styleSheet.contentContainer)}>
					<h1 className={css(styleSheet.headerTitle)}>Link Account in Chargify</h1>
					<div className={css(styleSheet.twoColumnLayout)}>
						<figure>
							<img width={528} height={528} src={nextDisplayImage} alt='new account image' />
						</figure>
						{offersLoading ? (
							<LoadingSpinner className={css(styleSheet.rightColumn)} />
						) : (
							<div className={css(styleSheet.rightColumn)}>
								<div className={css(styleSheet.fieldContainerStandard)}>
									<div className={css(styleSheet.chargifyField, styleSheet.formFieldLabel)}>Recently Used Links</div>
								</div>
								{renderChargifyOffers()}
								<div className={css(styleSheet.fieldContainerStandard)}>
									<div className={css(styleSheet.chargifyField)}>
										<div className={css(styleSheet.formFieldLabel)}>Chargify Offer Link</div>
										<input
											type='text'
											id='chargifyOfferLink'
											className={css(styleSheet.textBox)}
											placeholder='Enter Offer Link'
											value={selectedOffer ? selectedOffer.signupPage || '' : chargifyOfferLink}
											onChange={updateOfferLink}
											autoFocus={true}
										/>
									</div>
								</div>
								<footer className={css(styleSheet.buttonContainer, baseStyleSheet.horizontalStack)}>
									<button
										disabled={isFetchingSignUpUrl}
										className={css(baseStyleSheet.horizontalStack, baseStyleSheet.ctaButton)}
										onClick={createClick}
									>
										{isFetchingSignUpUrl ? <LoadingSpinner type='extra-tiny' /> : null}
										<span>Continue In Chargify</span>
									</button>
									{account && account.planDetails?.billingType !== Api.BillingType.Trial ? (
										<button className={css(baseStyleSheet.ctaButtonReverse)} onClick={() => setAccountComplete(true)}>
											Exit to Account Details
										</button>
									) : null}
								</footer>
							</div>
						)}
					</div>
				</div>
			);
		}
	};

	function createClick() {
		if (!selectedOffer && !chargifyOfferLink) {
			toast.error('Please select an offer or enter a link');
			return;
		}

		if ((selectedOffer?.signupPage || chargifyOfferLink || selectedOffer?.id) && account?.id) {
			setGetSignUpUrlEnabled(true);
		}
	}

	const updateOfferLink = (e: React.ChangeEvent<HTMLInputElement>) => {
		setSelectedOffer(undefined);
		setChargifyOfferLink(e.target.value);
	};

	React.useEffect(() => {
		if (!nextDisplayImage) {
			getRandomImage(nextDisplayImage, setNextDisplayImage, createAccountRequest?.displayImage);
		}
		return;
	}, [getRandomImage, nextDisplayImage, createAccountRequest?.displayImage, setNextDisplayImage]);

	React.useEffect(() => {
		if (signUpFetched && signUpUrl && getSignUpUrlEnabled) {
			setGetSignUpUrlEnabled(false);
			window.location.replace(signUpUrl);
		}
	}, [signUpFetched, signUpUrl, getSignUpUrlEnabled, setGetSignUpUrlEnabled]);

	if (!account) {
		return <Redirect to='/accounts' />;
	}

	return (
		<div className='admin-createAccount-container'>
			{renderFormContent()}
			{accountComplete ? renderCompleteRedirect() : null}
		</div>
	);
});
